import {
	PostProcessingsTilesEventBus,
	ProcessingsTilesEventBus,
} from './events';
import {
	FEATURE_GROUPS,
	PANES,
	PROCESSINGS_MIN_ZOOM,
} from '../leaflet/constants';
import { TILE_URL } from '../../globals';
import { PROCESSING_MAX_ZOOM } from '../processings/constants';
import { isEmpty, isEqual } from 'lodash/lang';
import { find, sortBy, forEach } from 'lodash/collection';
import { findIndex, remove } from 'lodash/array';
import { createQueryParams } from '../utils';
import { apiAuth } from '../../main';
import { LAYER_COLORS } from './constants';

const Wkt = require('wicket');
const wkt = new Wkt.Wkt();

const _Map = Symbol.for('_Map');

// #### Processings Results
const createProcessingsTilesUrl = (
	idProcessing,
	tileName,
	minVal = null,
	maxVal = null,
) => {
	const queryValues = {
		...(minVal ? { min_val: minVal } : {}),
		...(maxVal ? { max_val: maxVal } : {}),
	};
	const queryParams = createQueryParams(queryValues);
	let url;
	if (isEmpty(queryParams))
		url = `${TILE_URL}/tiles/get-processing-tile-shared/${idProcessing}/${tileName}/{z}/{x}/{y}`;
	else
		url = `${TILE_URL}/tiles/get-processing-tile-shared/${idProcessing}/${tileName}/{z}/{x}/{y}?${queryParams}`;
	return url;
};

const getProcessingsStorageItem = (obj, processing, tileName) => {
	return find(obj, (p) => {
		return (
			isEqual(
				p['processing']['id_processing'],
				processing['id_processing'],
			) && isEqual(p['tileName'], tileName)
		);
	});
};

export const ProcessingsTilesMixin = {
	inject: { _Map },
	data: () => ({
		processingStorage: [],
	}),
	methods: {
		addProcessingLayer(args) {
			const { processing, tileName } = args;

			const tileObject = getProcessingsStorageItem(
				this.processingStorage,
				processing,
				tileName,
			);
			if (tileObject) return;

			const layer = this.loadProcessingTiles(processing, tileName);
			this.processingStorage.push({
				processing: processing,
				tileName: tileName,
				layer: layer,
			});
			this.Map.sideBySide.idleLayers.push({
				processing: processing,
				tileName: tileName,
				layer: layer,
			});
			if (isEqual(this.Map.sideBySide.idleLayers.length, 2)) {
				let sortedArr = sortBy(this.Map.sideBySide.idleLayers, function(
					obj,
				) {
					return new Date(obj.processing['dh_created']);
				});
				if (isEmpty(this.Map.sideBySide.leftLayers))
					this.Map.sideBySide.leftLayers.push(sortedArr[0].layer);
				if (isEmpty(this.Map.sideBySide.rightLayers))
					this.Map.sideBySide.rightLayers.push(sortedArr[1].layer);
				this.Map.sideBySide.control.addTo(this.Map);
			}
		},
		removeProcessingLayer(args) {
			const { processing, tileName } = args;

			const tileObject = getProcessingsStorageItem(
				this.processingStorage,
				processing,
				tileName,
			);
			if (!tileObject) return;

			this.Map.featureGroups[FEATURE_GROUPS.PROCESSINGS].removeLayer(
				tileObject.layer,
			);

			this.Map.sideBySide.rightLayers.pop();
			this.Map.sideBySide.leftLayers.pop();

			const idx = findIndex(this.Map.sideBySide.idleLayers, (item) =>
				isEqual(
					item['layer']['_leaflet_id'],
					tileObject.layer._leaflet_id,
				),
			);
			this.Map.sideBySide.idleLayers.splice(idx, 1);

			this.Map.sideBySide.control.remove();

			remove(this.processingStorage, (p) => {
				return (
					isEqual(
						p['processing']['id_processing'],
						processing['id_processing'],
					) && isEqual(p['tileName'], tileName)
				);
			});
		},
		getProcessingBounds(processing) {
			const processingBounds = [
				processing['json_processing_extents']['coord_0']['longitude'],
				processing['json_processing_extents']['coord_0']['latitude'],
				processing['json_processing_extents']['coord_2']['longitude'],
				processing['json_processing_extents']['coord_2']['latitude'],
			];
			const firstCorner = window.L.latLng(
				processingBounds
					.slice(0, 2)
					.reverse()[0]
					.toString(),
				processingBounds
					.slice(0, 2)
					.reverse()[1]
					.toString(),
			);
			const lastCorner = window.L.latLng(
				processingBounds
					.slice(2, 4)
					.reverse()[0]
					.toString(),
				processingBounds
					.slice(2, 4)
					.reverse()[1]
					.toString(),
			);
			return window.L.latLngBounds(firstCorner, lastCorner);
		},
		loadProcessingTiles(processing, tileName) {
			const url = createProcessingsTilesUrl(
				processing['id_processing'],
				tileName,
			);
			let bounds = this.getProcessingBounds(processing);
			return window.L.tileLayer(url, {
				bounds: bounds,
				minZoom: PROCESSINGS_MIN_ZOOM,
				maxZoom: 26,
				maxNativeZoom: PROCESSING_MAX_ZOOM,
				tms: true,
				opacity: 1,
				pane: PANES.PROCESSINGS,
			}).addTo(this.Map.featureGroups[FEATURE_GROUPS.PROCESSINGS]);
		},
		updateProcessingLayer(args) {
			const { processing, tileName, minMax } = args;

			const tileObject = getProcessingsStorageItem(
				this.processingStorage,
				processing,
				tileName,
			);
			if (!tileObject) return;

			const url = createProcessingsTilesUrl(
				processing['id_processing'],
				tileName,
				minMax[0],
				minMax[1],
			);
			tileObject.layer.setUrl(url);
		},
		clear() {
			forEach(this.processingStorage, (item) => {
				this.Map.featureGroups[FEATURE_GROUPS.PROCESSINGS].removeLayer(
					item.layer,
				);
			});
			this.Map.sideBySide.rightLayers.splice(
				0,
				this.Map.sideBySide.rightLayers.length,
			);
			this.Map.sideBySide.leftLayers.splice(
				0,
				this.Map.sideBySide.leftLayers.length,
			);
			this.Map.sideBySide.idleLayers.splice(
				0,
				this.Map.sideBySide.idleLayers.length,
			);

			this.Map.sideBySide.control.remove();
			this.processingStorage.splice(0, this.processingStorage.length);
		},
	},
	computed: {
		Map() {
			return this._Map();
		},
	},
	mounted() {
		ProcessingsTilesEventBus.$on('add', this.addProcessingLayer);
		ProcessingsTilesEventBus.$on('remove', this.removeProcessingLayer);
		ProcessingsTilesEventBus.$on('update', this.updateProcessingLayer);
		ProcessingsTilesEventBus.$on('clear', this.clear);
	},
};

// #### Post Processings Results
const createPostProcessingsTilesUrl = (idPostProcessingAnalysisField) => {
	let authToken = '';
	try {
		authToken = apiAuth.getAuthToken();
	} catch (e) {
		authToken = '';
	}
	const queryValues = {
		access_token: authToken,
	};
	const queryParams = createQueryParams(queryValues);
	return `${TILE_URL}/tiles/get-post-processing-tile/${idPostProcessingAnalysisField}/{z}/{x}/{y}?${queryParams}`;
};

const getPostProcessingsStorageItem = (obj, idPostProcessingAnalysisField) => {
	return find(obj, (p) => {
		return isEqual(p['id'], idPostProcessingAnalysisField);
	});
};

export const PostProcessingsTilesMixin = {
	inject: { _Map },
	data: () => ({
		postProcessingStorage: [],
	}),
	methods: {
		addShapeLayer(args) {
			const { idPostProcessingAnalysisField, field } = args;

			const tileObject = getPostProcessingsStorageItem(
				this.postProcessingStorage,
				idPostProcessingAnalysisField,
			);
			if (tileObject) return;

			const layer = this.loadShapeTiles(
				idPostProcessingAnalysisField,
				field,
			);
			this.postProcessingStorage.push({
				id: idPostProcessingAnalysisField,
				layer: layer,
			});
		},
		removeShapeLayer(args) {
			const { idPostProcessingAnalysisField } = args;

			const tileObject = getPostProcessingsStorageItem(
				this.postProcessingStorage,
				idPostProcessingAnalysisField,
			);
			if (!tileObject) return;

			this.Map.featureGroups[
				FEATURE_GROUPS.POST_PROCESSINGS_RESULTS
			].removeLayer(tileObject.layer);

			remove(this.postProcessingStorage, (p) =>
				isEqual(p['id'], idPostProcessingAnalysisField),
			);
		},
		getFieldBounds(field) {
			if (!field) return;

			let geoJSON;
			if (field['geo_field'] instanceof Object)
				geoJSON = field['geo_field'];
			else geoJSON = wkt.read(field['geo_field']).toJson();

			const geoJSONLayer = window.L.GeoJSON.geometryToLayer(geoJSON, {});

			let options = {};
			options['fillColor'] = LAYER_COLORS.DEFAULT;
			options['color'] = LAYER_COLORS.DEFAULT;

			const polygon = window.L.polygon(
				geoJSONLayer.getLatLngs(),
				options,
			);

			return polygon.getBounds();
		},
		loadShapeTiles(idPostProcessingAnalysisField, field) {
			const url = createPostProcessingsTilesUrl(
				idPostProcessingAnalysisField,
			);
			let bounds = this.getFieldBounds(field);
			return window.L.vectorGrid
				.protobuf(url, {
					bounds: bounds,
					minZoom: 10,
					maxZoom: 20,
					pane: PANES.POST_PROCESSINGS_RESULTS,
				})
				.addTo(
					this.Map.featureGroups[
						FEATURE_GROUPS.POST_PROCESSINGS_RESULTS
					],
				);
		},
	},
	computed: {
		Map() {
			return this._Map();
		},
	},
	mounted() {
		PostProcessingsTilesEventBus.$on('add', this.addShapeLayer);
		PostProcessingsTilesEventBus.$on('remove', this.removeShapeLayer);
	},
};
